import React, { useEffect, useState } from 'react';
import { AlertTriangleIcon, AlertCircleIcon, CheckCircleIcon, Loader2Icon } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { InventoryAlert } from './types';
interface Props {
  restaurantId: string;
}
export function AlertsPanel({
  restaurantId
}: Props) {
  const [alerts, setAlerts] = useState<InventoryAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    async function fetchAlerts() {
      try {
        setLoading(true);
        const {
          data,
          error
        } = await supabase.from('v_inventory_alerts').select('*').eq('restaurant_id', restaurantId);
        if (error) throw error;
        setAlerts(data || []);
      } catch (err: any) {
        console.error('Error fetching alerts:', err);
        setError(err.message || 'Error al cargar alertas');
      } finally {
        setLoading(false);
      }
    }
    fetchAlerts();
  }, [restaurantId]);
  if (loading) {
    return <div className="flex items-center justify-center p-12" data-id="element-167">
        <Loader2Icon className="w-8 h-8 text-dorado animate-spin" data-id="element-168" />
      </div>;
  }
  if (error) {
    return <div className="p-6 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-center" data-id="element-169">
        <AlertCircleIcon className="w-8 h-8 mx-auto mb-2 opacity-80" data-id="element-170" />
        <p data-id="element-171">{error}</p>
      </div>;
  }
  if (alerts.length === 0) {
    return <div className="flex flex-col items-center justify-center p-16 bg-surface border border-gray-800 rounded-xl text-center" data-id="element-172">
        <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center mb-4" data-id="element-173">
          <CheckCircleIcon className="w-8 h-8 text-emerald-400" data-id="element-174" />
        </div>
        <h3 className="text-xl font-heading font-bold text-white mb-2" data-id="element-175">
          Todo en orden
        </h3>
        <p className="text-gray-400" data-id="element-176">
          No hay alertas activas de inventario en este momento.
        </p>
      </div>;
  }
  // Sort: CRITICAL first, then WARNING, then INFO
  const sortedAlerts = [...alerts].sort((a, b) => {
    const levelWeight = {
      CRITICAL: 3,
      WARNING: 2,
      INFO: 1
    };
    return levelWeight[b.alert_level] - levelWeight[a.alert_level];
  });
  const ingredientAlerts = sortedAlerts.filter(a => a.alert_type === 'INGREDIENT');
  const menuAlerts = sortedAlerts.filter(a => a.alert_type === 'MENU_ITEM');
  const AlertCard = ({
    alert
  }: {
    alert: InventoryAlert;
  }) => {
    const isCritical = alert.alert_level === 'CRITICAL';
    const isWarning = alert.alert_level === 'WARNING';
    const bgClass = isCritical ? 'bg-red-500/10 border-red-500/20' : isWarning ? 'bg-dorado/10 border-dorado/20' : 'bg-blue-500/10 border-blue-500/20';
    const textClass = isCritical ? 'text-red-400' : isWarning ? 'text-dorado' : 'text-blue-400';
    const Icon = isCritical ? XCircleIcon : AlertTriangleIcon;
    return <div className={`p-4 rounded-xl border ${bgClass} flex items-start gap-4`} data-id="element-177">
        <div className={`mt-0.5 ${textClass}`} data-id="element-178">
          <Icon className="w-5 h-5" data-id="element-179" />
        </div>
        <div className="flex-1" data-id="element-180">
          <div className="flex items-center justify-between mb-1" data-id="element-181">
            <h4 className={`font-bold ${textClass}`} data-id="element-182">{alert.item_name}</h4>
            <span className={`text-xs font-bold px-2 py-0.5 rounded-md border ${bgClass} ${textClass}`} data-id="element-183">
              {alert.alert_level}
            </span>
          </div>
          <p className="text-sm text-gray-300 mb-2" data-id="element-184">{alert.message}</p>
          {alert.alert_type === 'INGREDIENT' && <div className="flex items-center gap-4 text-xs font-medium" data-id="element-185">
              <span className="text-gray-400" data-id="element-186">
                Stock actual:{' '}
                <span className={textClass} data-id="element-187">{alert.current_stock}</span>
              </span>
              <span className="text-gray-500" data-id="element-188">
                Mínimo: {alert.minimum_stock}
              </span>
            </div>}
        </div>
      </div>;
  };
  return <div className="space-y-8" data-id="element-189">
      {ingredientAlerts.length > 0 && <section data-id="element-190">
          <h3 className="text-lg font-heading font-bold text-white mb-4 flex items-center gap-2" data-id="element-191">
            <PackageIcon className="w-5 h-5 text-gray-400" data-id="element-192" />
            Alertas de Ingredientes
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-193">
            {ingredientAlerts.map((alert, idx) => <AlertCard key={`ing-${idx}`} alert={alert} data-id="element-194" />)}
          </div>
        </section>}

      {menuAlerts.length > 0 && <section data-id="element-195">
          <h3 className="text-lg font-heading font-bold text-white mb-4 flex items-center gap-2" data-id="element-196">
            <UtensilsIcon className="w-5 h-5 text-gray-400" data-id="element-197" />
            Alertas de Menú
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-198">
            {menuAlerts.map((alert, idx) => <AlertCard key={`menu-${idx}`} alert={alert} data-id="element-199" />)}
          </div>
        </section>}
    </div>;
}